import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  TablePagination,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import {
  FormattedMessage, FormattedNumber, FormattedDate, useIntl,
} from 'react-intl';
import MyLinearProgress from '../MyLinearProgress';
import TooltipIconButton from '../TooltipIconButton';
import { MyFormattedDate, MyFormattedCurrency } from '../../intl';
import MyTableHead from './MyTableHead';
import { myTableHeadCellPropTypes } from './my-table-prop-types';
import pageUtilsPropTypes from '../../page/page-prop-types';
import MyAvatar from '../avatar/MyAvatar';
import MyTableRowCell from './MyTableRowCell';
import ImageThumbnail from '../ImageThumbnail';
import { seedMyTableIcons } from '../../icon/seed-icons';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 64,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(1),
  },
  tableRow: {
    '&:hover': {
      '& $showOnHover': {
        opacity: 1,
      },
    },
    '&:focus-within': {
      '& $showOnHover': {
        opacity: 1,
      },
    },
  },
  tableRowHover: {
    cursor: 'pointer',
  },
  spacer: {
    flexGrow: 1,
  },
  actionButton: {
    display: 'inline-block',
  },
  showOnHover: {
    opacity: 0,
    transition: theme.transitions.create(['opacity']),
  },
  loading: {
    opacity: 0,
    transition: theme.transitions.create(['opacity']),
  },
  loadingShow: {
    opacity: 1,
  },
  moreMenu: {
    minWidth: 240,
  },
}));

const MyTable = React.forwardRef((props, ref) => {
  const {
    id,
    titleId,
    tableSize,
    headers,
    pageUtils,
    loadData,

    defaultOrderBy,
    defaultOrderDirection,
    defaultPage,
    defaultRowsPerPage,

    onClickRow,

    enableEdit,
    onEdit,
    editIdentifier,

    enableDelete,
    onDelete,
    deleteIdentifier,

    enableMore,
    moreMenus,
    moreIdentifier,

    extraMenus,

    rowsPerPageOptions,
    actionsType,
    icons,
  } = props;
  const theme = useTheme();
  const intl = useIntl();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();

  const [error, setError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState(defaultOrderBy);
  const [orderDirection, setOrderDirection] = React.useState(defaultOrderDirection);
  const [page, setPage] = React.useState(defaultPage);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [count, setCount] = React.useState(0);

  const [moreRowId, setMoreRowId] = React.useState(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState(null);

  React.useEffect(() => {
    setError(false);
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => loadData(page, rowsPerPage, orderBy, orderDirection),
      (payload, response) => {
        if (Array.isArray(payload)) {
          setRows(payload);
          setCount(response.data.recordsTotal);
        } else {
          setRows([payload]);
          setCount(1);
        }
      },
      () => {
        setError(true);
      },
      () => {
        setIsLoading(false);
      },
    );


    return () => {

    };
    // eslint-disable-next-line
  }, [pageUtils]);

  let useFooter = false;
  headers.map((header) => {
    if (header.footer) {
      useFooter = true;
    }
    return null;
  });

  const handleRefresh = (
    newPage = page,
    newRowsPerPage = rowsPerPage,
    newOrderBy = orderBy,
    newOderDirection = orderDirection,
  ) => {
    setError(false);
    setIsLoading(true);
    pageUtils.loadSomethingSilent(
      () => loadData(newPage, newRowsPerPage, newOrderBy, newOderDirection),
      (payload, response) => {
        if (Array.isArray(payload)) {
          setRows(payload);
          setCount(response.data.recordsTotal);
        } else {
          setRows([payload]);
          setCount(1);
        }
      },
      () => {
        setError(true);
      },
      () => {
        setIsLoading(false);
      },
    );
  };

  React.useImperativeHandle(ref, () => ({
    refresh() {
      handleRefresh();
    },
  }));

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleRequestSort = (newOrderBy) => {
    const isAsc = orderBy === newOrderBy && orderDirection === 'asc';
    const newOrderDirection = isAsc ? 'desc' : 'asc';
    setOrderDirection(newOrderDirection);
    setOrderBy(newOrderBy);
    handleRefresh(page, rowsPerPage, newOrderBy, newOrderDirection);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleRefresh(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    handleRefresh(0, event.target.value);
  };

  const handleClickMore = (rowId, event) => {
    setMoreRowId(rowId);
    setMoreMenuAnchorEl(event.currentTarget);
  };

  return (
    <>
      <div className={classes.toolbar}>
        {
          titleId && !isMobile ? (
            <Typography variant="h6">
              <FormattedMessage id={titleId} />
            </Typography>
          ) : ''
        }
        <div className={classes.spacer} />
        <CircularProgress
          className={clsx(classes.loading, {
            [classes.loadingShow]: isLoading,
          })}
          size={20}
        />
        <TooltipIconButton
          size={tableSize}
          icon={<icons.Refresh />}
          disabled={isLoading}
          title={<FormattedMessage id="seed.MyTable.actions.refresh" />}
          onClick={() => handleRefresh()}
        />
      </div>
      <TableContainer>
        <Table
          size={tableSize}
        >
          <MyTableHead
            idTable={id}
            headers={headers}
            orderBy={orderBy}
            orderDirection={orderDirection}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            <TableRow>
              <TableCell
                align="center"
                padding="none"
                colSpan={headers.length}
              >
                <MyLinearProgress isLoading={isLoading} />
              </TableCell>
            </TableRow>
            {
              rows.length <= 0 && isLoading ? (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={headers.length}
                  >
                    <Typography color="textSecondary">
                      <FormattedMessage id="seed.MyTable.loading" />
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null
            }
            {
              rows.length <= 0 && !isLoading ? (
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={headers.length}
                  >
                    <Typography color="textSecondary">
                      <FormattedMessage id={error ? 'seed.MyTable.empty.error' : 'seed.MyTable.empty'} />
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null
            }
            {
              rows.length <= 0 ? null : rows.map((row, i) => (
                <TableRow
                // eslint-disable-next-line react/no-array-index-key
                  key={`myTable-${id}-row-${i}`}
                  classes={{
                    hover: classes.tableRowHover,
                  }}
                  className={classes.tableRow}
                // eslint-disable-next-line no-unneeded-ternary
                  hover={onClickRow ? true : false}
                  onClick={onClickRow ? (e) => onClickRow(e, row) : undefined}
                >
                  {
                headers.map((header) => {
                  switch (header.type) {
                    case 'actions':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-actions`}
                          padding={header.padding}
                          align={header.align}
                        >
                          {
                            !enableEdit ? '' : (
                              <TooltipIconButton
                                tooltipProps={{
                                  className: clsx(classes.actionButton, {
                                    [classes.showOnHover]: actionsType === 'showOnHover',
                                  }),
                                }}
                                size={tableSize}
                                icon={<icons.Edit />}
                                title={<FormattedMessage id="seed.MyTable.actions.edit" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row[editIdentifier], row);
                                }}
                              />
                            )
                          }
                          {
                            !enableDelete ? '' : (
                              <TooltipIconButton
                                tooltipProps={{
                                  className: clsx(classes.actionButton, {
                                    [classes.showOnHover]: actionsType === 'showOnHover',
                                  }),
                                }}
                                size={tableSize}
                                icon={<icons.Delete />}
                                title={<FormattedMessage id="seed.MyTable.actions.delete" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row[deleteIdentifier], row);
                                }}
                              />
                            )
                          }
                          {
                            extraMenus.map((menu) => (menu.condition(row) ? (
                              <TooltipIconButton
                                key={`table-${id}-extra-menu-${menu.id}`}
                                tooltipProps={{
                                  className: clsx(classes.actionButton, {
                                    [classes.showOnHover]: actionsType === 'showOnHover',
                                  }),
                                }}
                                size={tableSize}
                                icon={<menu.Icon />}
                                title={menu.text}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  menu.onClick(row[deleteIdentifier], row);
                                }}
                              />
                            ) : ''))
                          }
                          {
                            !enableMore ? '' : (
                              <TooltipIconButton
                                tooltipProps={{
                                  className: clsx(classes.actionButton, {
                                    [classes.showOnHover]: actionsType === 'showOnHover',
                                  }),
                                }}
                                size={tableSize}
                                icon={<icons.MoreVert />}
                                title={<FormattedMessage id="seed.MyTable.actions.more" />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickMore(row[moreIdentifier], e);
                                }}
                              />
                            )
                          }
                        </MyTableRowCell>
                      );
                    case 'currency':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <MyFormattedCurrency value={row[header.name]} />
                        </MyTableRowCell>
                      );
                    case 'number':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <FormattedNumber value={row[header.name]} />
                        </MyTableRowCell>
                      );
                    case 'date':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <MyFormattedDate value={new Date(row[header.name])} />
                        </MyTableRowCell>
                      );
                    case 'dateMonth':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <FormattedDate
                            value={new Date(row[header.name])}
                            year="numeric"
                            month="long"
                            // day="numeric"
                          />
                        </MyTableRowCell>
                      );
                    case 'avatar':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <MyAvatar
                            src={row[header.avatar.src]}
                            name={row[header.avatar.name]}
                            alt={row[header.avatar.alt]}
                          />
                        </MyTableRowCell>
                      );
                    case 'thumbnail':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          <ImageThumbnail
                            src={row[header.thumbnail.src]}
                            alt={row[header.thumbnail.alt]}
                          />
                        </MyTableRowCell>
                      );
                    case 'custom':
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          {header.onRender(row[header.name], row)}
                        </MyTableRowCell>
                      );
                    default:
                      return (
                        <MyTableRowCell
                          key={`myTable-${id}-cell-${header.labelId}`}
                          align={header.align}
                          padding={header.padding}
                        >
                          {row[header.name]}
                        </MyTableRowCell>
                      );
                  }
                })
              }
                </TableRow>
              ))
}
            {
            !useFooter ? null : (
              <TableRow>
                <TableCell />
                {
                  headers.map((header) => {
                    let value = 0;
                    switch (header.footer) {
                      case 'sum':
                        rows.map((row) => {
                          value += row[header.name];
                          return null;
                        });

                        return (
                          <TableCell
                            key={`myTable-${id}-footer-${header.labelId}`}
                            align={header.align}
                          >
                            <strong>
                              {
                                header.type === 'currency' ? (
                                  <MyFormattedCurrency value={value} />
                                ) : (
                                  <FormattedNumber value={value} />
                                )
                              }
                            </strong>
                          </TableCell>
                        );
                      default:
                        return (
                          <TableCell
                            key={`myTable-${id}-footer-${header.labelId}`}
                          />
                        );
                    }
                  })
                }
              </TableRow>
            )
          }
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={rowsPerPageOptions}
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        labelRowsPerPage={intl.formatMessage({ id: 'seed.MyTable.tablePagination.labelRowsPerPage' })}
        labelDisplayedRows={({ from, to, count: countRow }) => intl.formatMessage({
          id: 'seed.MyTable.tablePagination.labelDisplayedRows',
        },
        {
          from,
          to,
          count: countRow,
        })}
        nextIconButtonText={intl.formatMessage({ id: 'seed.MyTable.tablePagination.nextIconButtonText' })}
        backIconButtonText={intl.formatMessage({ id: 'seed.MyTable.tablePagination.backIconButtonText' })}
      />
      <Popover
        id={`table-${id}-more-menu`}
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
      >
        <div className={classes.moreMenu}>
          <List>
            {
              moreMenus.map((menu) => (
                <ListItem
                  key={`table-${id}-more-menu-${menu.id}`}
                  button
                  onClick={() => menu.onClick(moreRowId)}
                >
                  {
                    !menu.Icon ? '' : (
                      <ListItemIcon>
                        <menu.Icon />
                      </ListItemIcon>
                    )
                  }
                  <ListItemText primary={menu.text} />
                </ListItem>
              ))
            }
          </List>
        </div>
      </Popover>
    </>
  );
});

MyTable.propTypes = {
  id: PropTypes.string.isRequired,
  titleId: PropTypes.string,
  tableSize: PropTypes.oneOf(['small', 'medium']),
  headers: PropTypes.arrayOf(PropTypes.shape(myTableHeadCellPropTypes)),

  pageUtils: pageUtilsPropTypes.isRequired,
  loadData: PropTypes.func.isRequired,

  defaultOrderBy: PropTypes.string,
  defaultOrderDirection: PropTypes.string,
  defaultPage: PropTypes.number,
  defaultRowsPerPage: PropTypes.number,

  onClickRow: PropTypes.func,

  enableEdit: PropTypes.bool,
  onEdit: PropTypes.func,
  editIdentifier: PropTypes.string,

  enableDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  deleteIdentifier: PropTypes.string,

  enableMore: PropTypes.bool,
  moreMenus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      Icon: PropTypes.object,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  moreIdentifier: PropTypes.string,

  extraMenus: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      Icon: PropTypes.object,
      onClick: PropTypes.func.isRequired,
      condition: PropTypes.func.isRequired,
    }),
  ),

  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  actionsType: PropTypes.oneOf(['alwaysShow', 'showOnHover']),
  icons: PropTypes.shape({
    Refresh: PropTypes.object.isRequired,
    Edit: PropTypes.object.isRequired,
    Delete: PropTypes.object.isRequired,
    MoreVert: PropTypes.object.isRequired,
  }),
};

MyTable.defaultProps = {
  titleId: undefined,
  tableSize: 'medium',
  headers: [],

  defaultOrderBy: '',
  defaultOrderDirection: 'asc',
  defaultPage: 0,
  defaultRowsPerPage: 5,

  onClickRow: undefined,

  enableEdit: true,
  onEdit: undefined,
  editIdentifier: '_id',

  enableDelete: true,
  onDelete: undefined,
  deleteIdentifier: '_id',

  enableMore: false,
  moreMenus: [],
  moreIdentifier: '_id',

  extraMenus: [],

  rowsPerPageOptions: [5, 10, 25],
  actionsType: 'showOnHover',
  icons: seedMyTableIcons,
};

export default MyTable;
